import { Link, Navigate } from 'react-router-dom';
import { UserRole, UserStatus } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useProfile } from '@/hooks/use-profile';
import { PageContainer } from '@/components/PageContainer';
import { Button } from '@/components/ui/Button';
import { ShieldCheckIcon } from '@/components/ui/Icons';

/**
 * Holding page for an account that is verified but not yet usable — in
 * practice, a minor whose guardian has not approved the registration.
 * [FR-M01.2]
 */
export function PendingApprovalPage() {
  const { user, logout, reload } = useAuth();
  const { data } = useProfile();

  // Once the guardian approves, "Check again" flips the account to ACTIVE and
  // there is nothing left to hold. Without this the page would keep claiming
  // the account is inactive.
  if (user?.status === UserStatus.ACTIVE) {
    return <Navigate to="/profile" replace />;
  }

  const isTeenAwaitingGuardian = user?.role === UserRole.TEEN && user.awaitingParentApproval;
  const guardianEmail = data?.profile.invitedParentEmail ?? null;

  return (
    <PageContainer width="narrow">
      <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-lift">
        <span className="bg-brand-gradient mx-auto grid size-14 place-items-center rounded-2xl text-white">
          <ShieldCheckIcon className="size-7" />
        </span>

        <h1 className="mt-6 text-2xl font-extrabold text-ink">
          {isTeenAwaitingGuardian ? 'Waiting for your guardian' : 'Account not active yet'}
        </h1>

        <p className="mt-3 text-sm leading-relaxed text-slate-500">
          {isTeenAwaitingGuardian
            ? 'We have emailed your parent or guardian. Once they approve your account you will be able to browse and apply for opportunities.'
            : 'Your account is not active yet. If you have just verified your code, try refreshing.'}
        </p>

        {isTeenAwaitingGuardian && guardianEmail && (
          <p className="mt-4 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3 text-sm text-brand-800">
            We sent the request to <span className="font-semibold break-all">{guardianEmail}</span>.
            If that is wrong, ask them to check their spam folder — or update it from your profile.
          </p>
        )}

        <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={() => void reload()}>Check again</Button>
          <Button variant="outline" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>

        <Link
          to="/profile"
          className="mt-6 inline-block text-sm font-semibold text-brand-600 hover:text-brand-700"
        >
          Meanwhile, finish your profile
        </Link>
      </div>
    </PageContainer>
  );
}
